import { useEffect, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { subscribeToUpdates, subscribeToProjectEvents } from 'src/sockets/ContentGenSocket';
import { updateContentGenerationRecordInCache } from '../../Generate/components/ProductTable/useProductTable';

interface WebSocketUpdate {
  beaconClientId: string;
  status: string;
  skuContentGenerationId: string;
  retailerSku: string;
  profileName: string;
  profileId: string;
  profileVersion?: number;
  version?: number;
  updatedAt: string;
  projectId?: string;
}

interface UseProjectWebSocketOptions {
  projectId: string | null;
  retailerSkus: string[];
  enabled?: boolean;
  onStatusUpdate?: (update: WebSocketUpdate) => void;
}

interface ProjectEvent {
  projectId: string;
  name?: string;
  beaconClientId?: number;
  createdAt?: string;
  updatedAt?: string;
  createdBy?: string;
  updatedBy?: string;
}

/**
 * Hook to manage WebSocket subscriptions for project-specific content generation updates
 * Filters updates by projectId and retailerSku to only process relevant updates
 */
export const useProjectWebSocket = ({ projectId, enabled = true, onStatusUpdate }: UseProjectWebSocketOptions) => {
  const queryClient = useQueryClient();

  const invalidateProjectRelatedQueries = useCallback(() => {
    // Projects listing and single project details
    queryClient.invalidateQueries({ queryKey: ['projects'] });
    queryClient.invalidateQueries({ queryKey: ['project'] });
    // Service that tracks all generations across projects
    queryClient.invalidateQueries({ queryKey: ['allGenerationsService'] });
    // Content library data
    queryClient.invalidateQueries({ queryKey: ['ContentLibrary'], refetchType: 'active' });
    // Project review comparison data
    queryClient.invalidateQueries({ queryKey: ['PROJECT_REVIEW_COMPARE'], refetchType: 'active' });
    // Library review comparison data
    queryClient.invalidateQueries({ queryKey: ['LIBRARY_REVIEW_COMPARE'], refetchType: 'active' });
    // Content generation item queries
    queryClient.invalidateQueries({ queryKey: ['ContentGenerationItem'], refetchType: 'active' });
  }, [queryClient]);

  const handleMessage = useCallback(
    (data: WebSocketUpdate) => {
      const isMatchingProject = projectId && data.projectId === projectId;

      if (isMatchingProject) {
        if (onStatusUpdate) {
          onStatusUpdate(data);
        }

        // Update content generation queries directly in cache to avoid loading state
        updateContentGenerationRecordInCache(queryClient, data);
        // Invalidate other project-related queries that need refetching
        invalidateProjectRelatedQueries();
      }
    },
    [projectId, onStatusUpdate, invalidateProjectRelatedQueries, queryClient]
  );

  const handleError = useCallback((error: any) => {
    console.error('❌ Project WebSocket error:', error);
  }, []);

  const handleProjectEvent = useCallback(
    (event: ProjectEvent) => {
      const isMatchingProject = projectId && event.projectId === projectId;
      if (isMatchingProject) {
        invalidateProjectRelatedQueries();
      }
    },
    [projectId, invalidateProjectRelatedQueries]
  );

  useEffect(() => {
    if (!enabled || !projectId) {
      return;
    }

    let contentGenSubscription: { unsubscribe: () => void } | null = null;
    let projectEventsSubscription: { unsubscribe: () => void } | null = null;

    const setupSubscription = async () => {
      contentGenSubscription = await subscribeToUpdates({
        onMessage: handleMessage,
        onError: handleError
      });

      // Also listen to project-level events (create/update) and invalidate project queries
      projectEventsSubscription = await subscribeToProjectEvents({
        onMessage: handleProjectEvent,
        onError: handleError
      });
    };

    setupSubscription();

    // Cleanup: unsubscribe when component unmounts or dependencies change
    return () => {
      if (contentGenSubscription) {
        contentGenSubscription.unsubscribe();
      }
      if (projectEventsSubscription) {
        projectEventsSubscription.unsubscribe();
      }
    };
  }, [projectId, enabled, handleMessage, handleProjectEvent, handleError]);
};
